import React, { useRef } from "react";
import { Splide, SplideSlide, SplideTrack } from "@splidejs/react-splide";

import {
  EMessageActionType,
  ICarousel,
  ICarouselList,
  IMessageAction,
} from "@types";
import { Img } from "react-image";
import { ImageLoader } from "./ImageLoader";
import { createDataAttr } from "@utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { useShareLocation } from "@store/shareLocationStore";
import { useAppStore } from "@store/appStore";
import { shallow } from "zustand/shallow";
import { DEFAULT_TARGET } from "@constants";
import { resolveCarouselLayout } from "./richMessageLayout";

const BUTTON_DEFAULT_CLASS = "cursor-pointer break-words py-3 text-blue-700";

export type ICarouselMessageProps = {
  message: ICarouselList;
  linkTarget?: string;
  onSendAction: (action: IMessageAction) => void;
};

export const CarouselMessage = ({
  message,
  linkTarget = DEFAULT_TARGET,
  onSendAction,
}: ICarouselMessageProps) => {
  const { settings } = useAppStore(
    (state) => ({
      settings: state.settings,
    }),
    shallow,
  );
  const carouselLayout = resolveCarouselLayout(
    settings.richMessage,
    message.imageAspectRatio,
    message.columns?.length,
  );
  const carouselItemsRef = useRef<HTMLDivElement[]>([]);
  const { setIsOpen } = useShareLocation((state) => {
    return state;
  }, shallow);
  const _renderButton = (button: IMessageAction) => {
    if (button.type === EMessageActionType.DATE_TIME_PICKER) {
      <button
        className={BUTTON_DEFAULT_CLASS}
        style={carouselLayout.buttonStyle}
        {...createDataAttr("carousel-column-button")}
      >
        {button.label}
      </button>;
    }
    // link action
    if (button.type === EMessageActionType.URI_ACTION) {
      return (
        <a
          className={BUTTON_DEFAULT_CLASS}
          href={button.uri}
          style={carouselLayout.buttonStyle}
          target={linkTarget}
          {...createDataAttr("carousel-column-button")}
        >
          {button.label}
        </a>
      );
    }
    // call action
    if (button.type === EMessageActionType.CALL_ACTION) {
      return (
        <a
          className={BUTTON_DEFAULT_CLASS}
          href={`tel:${button.phoneNo}`}
          style={carouselLayout.buttonStyle}
          {...createDataAttr("carousel-column-button")}
        >
          {button.label}
        </a>
      );
    }
    // location action
    if (button.type === EMessageActionType.LOCATION_ACTION) {
      if (settings.allowLocation === false) {
        return null;
      }
      return (
        <button
          className={BUTTON_DEFAULT_CLASS}
          onClick={() => setIsOpen(true)}
          style={carouselLayout.buttonStyle}
        >
          {button.label}
        </button>
      );
    }

    return (
      <button
        className={BUTTON_DEFAULT_CLASS}
        onClick={(e) => {
          onSendAction(button);
        }}
        style={carouselLayout.buttonStyle}
        {...createDataAttr("carousel-column-button")}
      >
        {button.label}
      </button>
    );
  };

  const _renderImage = (element: ICarousel) => {
    // is square image
    if (message.imageAspectRatio === "square") {
      return (
        <Img
          className="w-full rounded-tl-[16px] rounded-tr-[16px] object-contain"
          src={element.thumbnailImageUrl}
          loading="lazy"
          loader={
            <ImageLoader
              width={carouselLayout.imageLoaderWidth}
              height={carouselLayout.imageLoaderHeight}
            />
          }
          style={{
            height: carouselLayout.imageHeightStyle,
            maxHeight: carouselLayout.imageHeightStyle,
            minHeight: carouselLayout.imageHeightStyle,
          }}
          {...createDataAttr("carousel-column-image")}
        />
      );
    }
    return (
      <Img
        className="w-full rounded-tl-[16px] rounded-tr-[16px] object-cover"
        src={element.thumbnailImageUrl}
        loading="lazy"
        loader={
          <ImageLoader
            width={carouselLayout.imageLoaderWidth}
            height={carouselLayout.imageLoaderHeight}
          />
        }
        style={{
          height: carouselLayout.imageHeightStyle,
          maxHeight: carouselLayout.imageHeightStyle,
          minHeight: carouselLayout.imageHeightStyle,
        }}
        {...createDataAttr("carousel-column-image")}
      />
    );
  };

  return (
    <div className="relative w-fit">
      <Splide
        hasTrack={false}
        options={{
          arrows: !carouselLayout.shouldHideArrows,
          gap: "10px",
          autoWidth: true,
          drag: "free",
          pagination: false,
          snap: false,
          omitEnd: true,
          focus: 0,
          breakpoints: {
            200: {
              width: "250px",
            },
            380: {
              width: "300px",
            },
            460: {
              width: "400px",
            },
            640: {
              width: "580px",
            },
            840: {
              width: "771px",
            },
          },
          mediaQuery: "min",
        }}
        {...createDataAttr("carousel-message")}
      >
        <SplideTrack>
          {message?.columns?.map((element: ICarousel, index: number) => {
            return (
              <SplideSlide
                key={element.id}
                className="flex flex-col"
                style={{
                  width: carouselLayout.cardWidthStyle,
                }}
                {...createDataAttr("carousel-column")}
              >
                {_renderImage(element)}
                <div
                  ref={(ref: HTMLDivElement) =>
                    (carouselItemsRef.current[index] = ref as HTMLDivElement)
                  }
                  className="flex flex-1 flex-col justify-between break-keep rounded-bl-[16px] rounded-br-[16px] bg-white"
                >
                  <div
                    {...createDataAttr("carousel-column-detail")}
                    className="border-b border-solid border-[rgb(229,229,229)] p-[14px]"
                  >
                    <div
                      title={element.title}
                      className="text-abrown overflow-hidden text-ellipsis whitespace-pre-wrap text-[14px] font-medium text-gray-800"
                      {...createDataAttr("carousel-column-detail-title")}
                    >
                      {element.title}
                    </div>
                    <div
                      title={element.text}
                      style={{
                        marginTop: element.title ? "8px" : 0,
                      }}
                      className="text-abrown  overflow-hidden text-ellipsis whitespace-pre-wrap text-[14px] text-gray-500"
                      {...createDataAttr("carousel-column-detail-description")}
                    >
                      {element.text}
                    </div>
                  </div>
                  <div className={`flex flex-col items-center`}>
                    {element.actions.map((button: IMessageAction) => {
                      return (
                        <React.Fragment key={button.id}>
                          {_renderButton(button)}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              </SplideSlide>
            );
          })}
        </SplideTrack>
        {!carouselLayout.shouldHideArrows ? (
          <div className="splide__arrows">
            <button className="splide__arrow splide__arrow--prev left-[-35px]">
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
            <button className="splide__arrow splide__arrow--next right-[-35px]">
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        ) : null}
      </Splide>
    </div>
  );
};
